import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    Box,
    useTheme,
} from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { MediaTable } from "src/pages/Settings/MediaLibrary/components/MediaTable";
import {
    MediaItem,
    MediaLibraryContextProvider,
    useMediaLibraryContext,
} from "src/pages/Settings/MediaLibrary/context";
import { CREATE, MEDIA_CONTENT, useFoodpilotContext } from "src/context/FoodpilotContext";

export type MediaManagerDialogProps = {
    open: boolean;
    onClose: () => void;
    onSelect: (item: MediaItem) => void;
    allowedMimeTypes?: string[];
    allowedExtensions?: string[];
    title?: string;
    showUploadButton?: boolean;
    fullWidth?: boolean;
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
};

const MediaManagerDialogContent = (props: MediaManagerDialogProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const mediaLibraryContext = useMediaLibraryContext();
    const { canUser } = useFoodpilotContext();
    const canCreate = canUser(CREATE, MEDIA_CONTENT);

    const {
        open,
        onClose,
        onSelect,
        allowedMimeTypes,
        allowedExtensions,
        title,
        showUploadButton,
        fullWidth,
        maxWidth,
    } = props;

    const isMediaAllowed = useMemo(() => {
        return (item: MediaItem): boolean => {
            if (!allowedMimeTypes && !allowedExtensions) return true;

            if (allowedMimeTypes && item.mimeType) {
                const isAllowedMimeType = allowedMimeTypes.some((allowedType) => {
                    if (allowedType.endsWith("/*")) {
                        const baseType = allowedType.replace("/*", "");
                        return item.mimeType?.startsWith(baseType);
                    }
                    return item.mimeType === allowedType;
                });
                if (isAllowedMimeType) return true;
            }

            if (allowedExtensions && item.name) {
                const fileExtension = item.name.split(".").pop()?.toLowerCase();
                if (fileExtension && allowedExtensions.includes(fileExtension)) {
                    return true;
                }
            }

            return false;
        };
    }, [allowedMimeTypes, allowedExtensions]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            sx={{
                "& .MuiPaper-root": {
                    borderRadius: theme.shape.borderRadius,
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3">{title || t("puck.media_manager.title", "Select media")}</Typography>
                    {showUploadButton && canCreate && (
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="primary"
                                onClick={() => mediaLibraryContext.setIsUploadDialogOpen(true)}
                                size="small"
                                startIcon={<FoodsIcon icon="uploadFile" size={2} />}
                                disabled={mediaLibraryContext.isUploading}
                            >
                                {t("upload", "Upload File")}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ height: "70vh", p: 0 }}>
                <Box sx={{ height: "100%", p: theme.spacing(2) }}>
                    <MediaTable filterFunction={isMediaAllowed} rowClickHandler={mediaLibraryContext.onSelectItem} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="ternary" onClick={onClose}>
                    {t("cancel", "Cancel Selection")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const MediaManagerDialog = (props: MediaManagerDialogProps) => {
    const { onSelect, onClose, ...rest } = props;

    const handleSelectItem = (item: MediaItem) => {
        onSelect(item);
        onClose();
    };

    return (
        <MediaLibraryContextProvider onSelectItem={handleSelectItem} allowedMimeTypes={props.allowedMimeTypes}>
            <MediaManagerDialogContent onSelect={onSelect} onClose={onClose} {...rest} />
        </MediaLibraryContextProvider>
    );
};
